import React, { useEffect, useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const SUPPORT_EMAIL = "n.anderson@qub.ac.uk";

export default function SurveyHelpBanner() {
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadDismissalPreference() {
      const user = auth.currentUser;
      if (!user) {
        if (active) setDismissed(false);
        return;
      }

      try {
        const profile = await getDoc(doc(db, "users", user.uid));
        if (active) {
          setDismissed(profile.exists() && profile.data()?.surveyHelpBannerDismissed === true);
        }
      } catch {
        if (active) setDismissed(false);
      }
    }

    loadDismissalPreference();
    return () => {
      active = false;
    };
  }, []);

  const handleDismiss = async () => {
    const user = auth.currentUser;
    if (!user || dismissing) return;

    setDismissing(true);
    setDismissError(false);
    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          surveyHelpBannerDismissed: true,
          surveyHelpBannerDismissedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setDismissed(true);
    } catch {
      setDismissError(true);
      setDismissing(false);
    }
  };

  if (dismissed === null || dismissed) return null;

  return (
    <Box
      component="aside"
      aria-label="Survey development help"
      sx={{
        width: { xs: "100%", md: "75%" },
        mx: "auto",
        mb: 3,
        p: { xs: 2, sm: 2.5 },
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: "rgba(25, 118, 210, 0.08)",
        border: 1,
        borderColor: "primary.main",
        borderRadius: 1,
        boxSizing: "border-box",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 38,
          height: 38,
          flex: "0 0 auto",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: "1.4rem",
          fontWeight: 700,
        }}
      >
        ?
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1">
          For help and assistance developing your survey, please contact Neil Anderson in the WHO
          Collaborating Centre (CC) at QUB.
        </Typography>
        <Link
          href={`mailto:${SUPPORT_EMAIL}`}
          sx={{
            mt: 0.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontWeight: 600,
          }}
        >
          <EmailOutlinedIcon fontSize="small" aria-hidden="true" />
          {SUPPORT_EMAIL}
        </Link>
        {dismissError && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
            We couldn&apos;t save your preference. Please try again.
          </Typography>
        )}
      </Box>

      <Button
        type="button"
        size="small"
        variant="text"
        startIcon={<CloseIcon />}
        onClick={handleDismiss}
        disabled={dismissing}
        sx={{ flex: "0 0 auto", alignSelf: { xs: "flex-start", sm: "center" } }}
      >
        {dismissing ? "Dismissing…" : "Dismiss message"}
      </Button>
    </Box>
  );
}
